import * as React from 'react'

import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import type { FilterOption } from './computeOptions'
import computeOptions from './computeOptions'
import { FilterProps } from './types'

type Props = FilterProps & {
  field: Field
  canSeeClockedIn: boolean
}

const concatOptionsWithResult = (
  options: Record<string, FilterOption[]>,
  result: Record<string, FilterOption[]>
): Record<string, FilterOption[]> => {
  // We do it that way to get the custom options at the beginning of the select
  // and the default ones at the end
  Object.entries(result).forEach(([resultKey, value]) => {
    const optionsValue = options[resultKey]

    if (optionsValue) {
      options[resultKey] = optionsValue.concat(value)
    } else {
      options[resultKey] = value
    }
  })

  return options
}

const Filters = ({
  none,
  field,
  defaultOption,
  width = 's120',
  options = {},
  hideFilterByStatus,
  hideFirstEmployeeFilter,
  showFilterByActive,
  showFilterByAway,
  showFilterByAwayThisMonth,
  showFilterByDirectManagedEmployee,
  showFilterByLegalEntity,
  showFilterByManagedEmployee,
  showFilterByTeam,
  hideClockedInFilter,
  showShiftConflictsFilter,
  showFilterByTimeoffSupervisor,
  showFilterByWelcomeSpaceAccess,
  showFilterByLocationIssues,
  canSeeClockedIn,
  onChange,
}: Props) => {
  const [computedOptions, setComputedOptions] = React.useState<Record<string, FilterOption[]>>({})

  React.useEffect(() => {
    onChange && onChange(field.value)
  }, [field.value])

  const { loading } = useAsyncData(async () => {
    setComputedOptions(
      concatOptionsWithResult(
        options,
        await computeOptions({
          hideFilterByStatus,
          hideFirstEmployeeFilter,
          showFilterByActive,
          showFilterByAway,
          showFilterByAwayThisMonth,
          showFilterByDirectManagedEmployee,
          showFilterByLegalEntity,
          showFilterByManagedEmployee,
          showFilterByTeam,
          hideClockedInFilter,
          showShiftConflictsFilter,
          showFilterByTimeoffSupervisor,
          showFilterByWelcomeSpaceAccess,
          showFilterByLocationIssues,
          canSeeClockedIn,
        })
      )
    )
  }, [])

  return (
    <Box width={width}>
      {loading ? (
        <Skeleton width="s96" height="s32" />
      ) : (
        <Select
          none={none}
          defaultOption={defaultOption}
          options={computedOptions}
          field={field}
          showOriginalLabelText
        />
      )}
    </Box>
  )
}

export default observer(Filters)
